// src/components/EnhancedFooter.tsx
import { ForceReloadLink } from './ForceReloadLink.tsx';
import { FullLogo } from './Logo';

export function EnhancedFooter() {
  const currentYear = new Date().getFullYear();
  
  // Improved CSS class with stronger specificity for visited links
  const footerLinkClass = "text-gray-600 hover:text-primary active:text-gray-600 visited:!text-gray-600 focus:text-gray-600 text-sm no-underline";
  
  return (
    <>
      {/* Desktop Footer (hidden on mobile) */}
      <footer className="bg-white border-t border-gray-200 pt-12 pb-6 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {/* Logo and About Section - reduced width */}
            <div className="md:col-span-3">
              <ForceReloadLink to="/" className="flex items-center mb-4">
                <FullLogo width={40} height={40} />
                <span className="ml-3 text-xl font-semibold text-gray-900">BuyHomeCalc</span>
              </ForceReloadLink>
              <p className="text-gray-600 text-sm mb-4">
                Interactive tools to help you make smarter home buying decisions.
              </p>
              <p className="text-sm text-gray-500 mb-4">
                © {currentYear} BuyHomeCalc.com. All rights reserved.
              </p>
            </div>

            {/* Add spacing */}
            <div className="md:col-span-1"></div>

            {/* Calculators Section - moved closer */}
            <div className="md:col-span-2">
              <h3 className="text-base font-semibold text-primary mb-3">Calculators</h3>
              <ul className="space-y-1">
                <li>
                  <ForceReloadLink to="/home-affordability-calculator" className={footerLinkClass}>Home Affordability</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/mortgage-payment-calculator" className={footerLinkClass}>Mortgage Calculator</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/purchase-timeline-calculator" className={footerLinkClass}>Future Purchase</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/down-payment-calculator" className={footerLinkClass}>Down Payment</ForceReloadLink>
                </li>
              </ul>
            </div>

            {/* Embed Tools Section - new section */}
            <div className="md:col-span-2">
              <h3 className="text-base font-semibold text-primary mb-3">Embed Tools</h3>
              <ul className="space-y-1">
                <li>
                  <ForceReloadLink to="/home-affordability-embed" className={footerLinkClass}>Affordability Embed</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/mortgage-payment-embed" className={footerLinkClass}>Mortgage Embed</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/purchase-timeline-embed" className={footerLinkClass}>Future Purchase Embed</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/down-payment-embed" className={footerLinkClass}>Down Payment Embed</ForceReloadLink>
                </li>
              </ul>
            </div>

            {/* Support Section - Updated with About link */}
            <div className="md:col-span-2">
              <h3 className="text-base font-semibold text-primary mb-3">Support</h3>
              <ul className="space-y-1">
                <li>
                  <ForceReloadLink to="/about" className={footerLinkClass}>About Us</ForceReloadLink>
                </li>
                <li>
                  <a href="mailto:etfscommunity@gmail.com" className={footerLinkClass}>Contact</a>
                </li>
                <li>
                  <a href="https://www.buymeacoffee.com/etfscommunity" target="_blank" rel="noopener noreferrer" className={footerLinkClass}>Support This Project</a>
                </li>
              </ul>
            </div>

            {/* Legal Section - moved closer */}
            <div className="md:col-span-2">
              <h3 className="text-base font-semibold text-primary mb-3">Legal</h3>
              <ul className="space-y-1">
                <li>
                  <ForceReloadLink to="/terms" className={footerLinkClass}>Terms of Service</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/privacy" className={`${footerLinkClass} !text-gray-600`}>Privacy Policy</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/disclaimer" className={footerLinkClass}>Disclaimer</ForceReloadLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile Footer (hidden on desktop) */}
      <footer className="bg-white border-t border-gray-200 pt-6 pb-6 md:hidden">
        <div className="px-4">
          {/* Logo and About Section */}
          <div className="flex flex-col items-center text-center">
            <ForceReloadLink to="/" className="flex items-center mb-3">
              <FullLogo width={32} height={32} />
              <span className="ml-2 text-lg font-semibold text-gray-900">BuyHomeCalc</span>
            </ForceReloadLink>
            <p className="text-gray-600 text-xs mb-6 max-w-xs">
              Interactive tools to help you make smarter home buying decisions.
            </p>
          </div>
          
          {/* Page links in a grid layout */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-6 mb-6">
            {/* Calculators Section */}
            <div>
              <h3 className="text-base font-semibold text-primary mb-3">Calculators</h3>
              <ul className="space-y-1">
                <li>
                  <ForceReloadLink to="/home-affordability-calculator" className={footerLinkClass}>Home Affordability</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/mortgage-payment-calculator" className={footerLinkClass}>Mortgage Calculator</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/purchase-timeline-calculator" className={footerLinkClass}>Future Purchase</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/down-payment-calculator" className={footerLinkClass}>Down Payment</ForceReloadLink>
                </li>
              </ul>
            </div>
            
            {/* Embed Tools Section */}
            <div>
              <h3 className="text-base font-semibold text-primary mb-3">Embed Tools</h3>
              <ul className="space-y-1">
                <li>
                  <ForceReloadLink to="/home-affordability-embed" className={footerLinkClass}>Affordability Embed</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/mortgage-payment-embed" className={footerLinkClass}>Mortgage Embed</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/purchase-timeline-embed" className={footerLinkClass}>Purchase Date Embed</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/down-payment-embed" className={footerLinkClass}>Down Payment Embed</ForceReloadLink>
                </li>
              </ul>
            </div>
            
            {/* Support Section */}
            <div>
              <h3 className="text-base font-semibold text-primary mb-3">Support</h3>
              <ul className="space-y-1">
                <li>
                  <ForceReloadLink to="/about" className={footerLinkClass}>About Us</ForceReloadLink>
                </li>
                <li>
                  <a href="mailto:etfscommunity@gmail.com" className={footerLinkClass}>Contact</a>
                </li>
                <li>
                  <a href="https://www.buymeacoffee.com/etfscommunity" target="_blank" rel="noopener noreferrer" className={footerLinkClass}>Support This Project</a>
                </li>
              </ul>
            </div>
            
            {/* Legal Section */}
            <div>
              <h3 className="text-base font-semibold text-primary mb-3">Legal</h3>
              <ul className="space-y-1">
                <li>
                  <ForceReloadLink to="/terms" className={footerLinkClass}>Terms of Service</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/privacy" className={`${footerLinkClass} !text-gray-600`}>Privacy Policy</ForceReloadLink>
                </li>
                <li>
                  <ForceReloadLink to="/disclaimer" className={footerLinkClass}>Disclaimer</ForceReloadLink>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Copyright line at the bottom */}
          <div className="text-center">
            <p className="text-xs text-gray-500">
              © {currentYear} BuyHomeCalc.com. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}